import {action, axios, computed, observable, runInAction} from '@api'
import {CatalogItemsPDFTemplate, ICloudPDFEntry, IServerPDFQueryPayload, IServerRenderedPDFTemplateDefinition, IServerRenderedPDFTemplateInstance, PDFArtifactID, PDFServerRenderedContainerStore, PDFTemplateName, AssortmentPDFTemplate, ServerPDFTemplate, CatalogItemsGroupedPDFTemplate} from '@modules/pdf'
import {QUERY_KEY_PDF_ARTIFACTS} from '@modules/pdf/serverRenderedPDF/components'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {AxiosInstance} from 'axios'
import {autorun} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {Socket} from 'socket.io-client'
import type {IPdfStatusUpdatePayload, PDFJobStatus} from '@nest/socket-io.gateway'
import {_} from '../../../shared/components/3rd-party'
import {CloudPDFEntry} from './'
import {DefaultLayoutPlugin, defaultLayoutPlugin} from '@react-pdf-viewer/default-layout'
import {aws, CatalogSerializableSettings, CatalogStore, globalStores, PdfPageOrientation, PdfPageSizes, StoresInstance, ui} from '@stores'
import {guid} from '@utils'

const ITEMS_PER_PAGE = 50

export type PdfJobID = string

export interface IRenderPDFServerSidePayload {
  index: string
  body: { query: any }
  template: string
  settings: any
  userId: string
  title?: string
  author?: string
  subject?: string
  keywords?: string
  filename?: string
}

export type IServerPDFReportJobPayload = {
  userId: string
  title?: string
  author?: string
  subject?: string
  keywords?: string
  filename?: string
  pageSize?: PdfPageSizes
  orientation?: PdfPageOrientation

  templates?: Array<PDFTemplateJobPayload>
}

export interface PdfCoverPageJobPayload extends BasePDFTemplateJobPayload {
  template: 'coverPage'
  title?: string,
  subtitle?: string
}

export interface QRCodeReportPageJobPayload extends BasePDFTemplateJobPayload {
  template: 'qrcode-report',
  reportId: string
}

export type PDFTemplateJobPayload = CatalogPDFTemplateJobPayload | PdfCoverPageJobPayload | QRCodeReportPageJobPayload

export interface BasePDFTemplateJobPayload {
  template: PDFTemplateName
}

export type CatalogPDFTemplateNames = 'assortment' | 'catalogItems' | 'catalogItems_grouped'

export interface CatalogPDFTemplateJobPayload extends BasePDFTemplateJobPayload {
  template: CatalogPDFTemplateNames,
  index: string
  body: { query: any }
  settings?: Partial<CatalogSerializableSettings>
  // includeQRCode?: boolean
  // individualPDFName?: string
}

export interface IQueuedPdfServerJob {
  /* Specific to showing a toast popup */
  toastId?: string
  hideToast?: boolean
  error?: any

  jobId?: string,
  tabId?: string,
  pdfInput?: IServerPDFQueryPayload | IServerPDFReportJobPayload
  status?: PDFJobStatus
  lastStatusUpdate?: IPdfStatusUpdatePayload
  defaultFilename?: string

  result?: IPdfStatusUpdatePayload
}

export interface ISubmitPdfJobResponse {
  data: {
    ResponseMetadata: {
      RequestId: string,
    },
    MD5OfMessageBody: string,
    MessageId: string
    jobId: string
  }
}

export class CloudPDFStore {
  constructor(public catalog: CatalogStore) {
    synchronizeStores(() => {
      catalog.stores.isGlobalStore && autorun(() => {
        const {stores: {socketIO: {client}, userSettings: {serverPDF}, deployments: {deploymentConfig}}} = catalog, {_priorSocketIOClient} = this

        if (deploymentConfig?.settings?.pdf?.target === 'server' || serverPDF) {
          if (client && client != this._priorSocketIOClient) {
            if (_priorSocketIOClient) {
              _priorSocketIOClient.off('pdfStatus', this.onPdfStatus)
              // console.log(`Unhook event handler to `, client)
            }

            // console.log(`Hook event handler to `, client)
            client.on('pdfStatus', this.onPdfStatus)
            this._priorSocketIOClient = client
          }
        }
      })

      this._toDispose.push(
        autorun(() => {
          if (this.enabled) {
            const {} = this
            this.loadPdfRecords({pageParam: 0})
          }
        })
      )
    })
  }

  dispose = () => {
    this._toDispose.forEach(v => v())
  }

  _toDispose: Array<IDisposer> = []
  private _priorSocketIOClient: Socket

  get pendingJobs() {
    return this.stores.currentUser?.pendingPdfJobs ?? []
  }

  @action registerServerPdfJob = (job: IQueuedPdfServerJob & { result?: IPdfStatusUpdatePayload }) => {
    this.stores.currentUser.pendingPdfJobs.push(job)

    return _.last(this.stores.currentUser.pendingPdfJobs)
  }

  @computed get jobByTab() {
    return _.groupBy(this.pendingJobs.slice(), j => j.tabId)
  }

  @computed get jobById(): Record<PdfJobID, IQueuedPdfServerJob> {
    return _.keyBy(this.pendingJobs.slice(), j => j.jobId)
  }

  onPdfStatus = async (data: IPdfStatusUpdatePayload) => {
    const {stores: {site, queryClient, users: {user: {id: userId}}}, jobById} = this,
      {jobId, s3Key, s3DocumentUrl} = data

    // console.log(`onPdfStatus`, {...data})

    let job = jobById[jobId]

    if (job) {
      job.status = data.status
      job.lastStatusUpdate = data
    }

    if (data.status === 'done' && data.userId === userId) {
      if (!job) {
        job = this.registerServerPdfJob({
            jobId,
            lastStatusUpdate: data
          }
        )
      }

      job.toastId = guid()
      job.hideToast = false

      //window.open(aws.urlFor(s3Key))
      await this.loadPdfRecord({id: data.filename})
      await queryClient.invalidateQueries(QUERY_KEY_PDF_ARTIFACTS)
    }
  }

  templatesById: Record<PDFTemplateName, (stores: StoresInstance) => ServerPDFTemplate> = {
    assortment: (stores: StoresInstance) => new AssortmentPDFTemplate(stores),
    catalogItems: (stores: StoresInstance) => new CatalogItemsPDFTemplate(stores),
    catalogItems_grouped: (stores: StoresInstance) => new CatalogItemsGroupedPDFTemplate(stores)
  }

  get stores() { return this.catalog.stores }

  @computed get axiosClient(): AxiosInstance {
    const {
      stores: {currentUser: {userSettings: {dev}}, deployments: {deploymentConfig: {pdf}}}
    } = this

    if (pdf?.renderApi) {
      const {renderApi: {baseUrl, apiKey}} = pdf,
        client = axios.create({
          baseURL: DEBUG && dev?.localSAM ? 'http://localhost:3000' : baseUrl
        })

      const {defaults: {headers}} = client
      headers['Content-Type'] = 'application/json'
      //headers['x-c8-auth'] = authStore.accessToken
      headers['x-api-key'] = apiKey

      return client
    }

    return null
  }

  @observable.ref component: PDFServerRenderedContainerStore

  get availableTemplates() {
    return this.catalog.pdfCatalogStore.availableTemplates
  }

  @computed get enabled() {
    return this.stores?.deployments?.deploymentConfig?.settings?.pdf?.target === 'server'
  }

  @action loadPdfRecord = async ({id}: { id: string }) => {
    const {stores, stores: {es, users: {user}}} = this

    const entry = await es.get<ICloudPDFEntry>({
      index: es.indexes.pdfArtifact,
      id
    })

    entry && CloudPDFStore.generatedPdfs.set(id, new CloudPDFEntry(entry, stores))

    return entry
  }

  @action loadPdfRecords = async ({pageParam = 0}) => {
    const {stores, stores: {es, users: {user}}} = this

    const {items} = es.unwrapHitsResult<ICloudPDFEntry>(
      await es.search({
        index: es.indexes.pdfArtifact,
        body: {
          from: pageParam * ITEMS_PER_PAGE,
          size: ITEMS_PER_PAGE,
          query: {
            term: {'userId.keyword': user.id}
          },
          sort: {timestamp: {order: 'desc'}}
        },
        meta: 'Load generated PDF list'
      }))

    return runInAction(() => {
      const r = items.map(v => new CloudPDFEntry(v, stores))
      r.forEach(v => CloudPDFStore.generatedPdfs.set(v.entry._id, v))
      return r
    })
  }

  static generatedPdfs = observable.map<PDFArtifactID, CloudPDFEntry>()

  get generatedPdfs() { return CloudPDFStore.generatedPdfs }

  @computed get pdfsTimestampDesc() {
    return Array.from(CloudPDFStore.generatedPdfs.values()).sort((a, b) => a.timestamp - b.timestamp)
  }

  @observable.ref defaultLayoutPluginInstance: DefaultLayoutPlugin

  @action prepareGeneratePDF = async (queryPayload: IRenderPDFServerSidePayload) => {
    const {
      stores: {site, deployments: {deploymentConfig: {pdf}}, catalogTab},
      axiosClient, catalog: {settings}
    } = this
    if (!pdf?.renderApi || !axiosClient) throw new Error(`Deployment config is missing required field: pdf.renderApiUrl`)

    const {renderApi: {host, env}} = pdf,
      serverJob = this.registerServerPdfJob({pdfInput: queryPayload, tabId: catalogTab?.tab?.id, status: 'pending', defaultFilename: queryPayload?.filename})

    return serverJob
  }

  @action submitPdfJob = async (job: IQueuedPdfServerJob) => {
    const {
      axiosClient, stores: {currentUser: {userSettings: {dev}}, deployments: {deploymentConfig: {pdf: {renderApi: {host, env}}}}}
    } = this

    job.status = 'submitting'
    try {
      const r = await axiosClient.post<any, ISubmitPdfJobResponse>(
        `${dev?.localSAM ? '' : '/Prod'}/pdf/submitJob?host=${host}&env=${env}${dev?.localSAM ? '&go' : ''}`,
        {...job.pdfInput, filename: job.pdfInput?.filename || job.defaultFilename}
      )
      // console.log({r, job})

      runInAction(() => {
        job.jobId = r.data.jobId
        job.status = 'accepted'
      })
    } catch (err) {
      console.log(`PDF job failed to submit - `, err, job)
      job.status = 'failed'
      job.error = err
    }
  }

  @action submitCurrentReportPDFJob = async () => {
    const {
      stores: {
        ui: {settings}, currentUser: {userSettings: {dev}}, reports: {currentReport},
        deployments: {deploymentConfig: {pdf}}
      },
      axiosClient
    } = this

    if (!pdf?.renderApi || !axiosClient) throw new Error(`Deployment config is missing required field: pdf.renderApiUrl`)

    currentReport.aboutToSubmitPdfJob = true

    setTimeout(async () => {
      const {renderApi: {host, env}} = pdf,
        {stores: {reports: {currentReportPDFPayload}}} = this,
        serverJob = this.registerServerPdfJob({
          pdfInput: currentReportPDFPayload,
          status: 'submitting'
        })

      const r = await axiosClient.post<any, ISubmitPdfJobResponse>(
        `${dev?.localSAM ? '' : '/Prod'}/pdf/submitJob?host=${host}&env=${env}&reportId=${currentReport.id}${dev?.localSAM ? '&go' : ''}`,
        currentReportPDFPayload
      )

      runInAction(() => {
        serverJob.jobId = r.data.jobId
        serverJob.status = 'accepted'
        console.log({r, serverJob})
      })

      currentReport.serverPdfJob = serverJob
    }, 100)
  }

  @action generatePDF = async (queryPayload: IRenderPDFServerSidePayload) => {
    const {
      stores: {currentUser: {userSettings: {dev}}, ui: {settings}, deployments: {deploymentConfig: {pdf}}, catalogTab},
      axiosClient
    } = this
    if (!pdf?.renderApi || !axiosClient) throw new Error(`Deployment config is missing required field: pdf.renderApiUrl`)

    const {renderApi: {host, env}} = pdf,
      serverJob = this.registerServerPdfJob({pdfInput: queryPayload, tabId: catalogTab?.tab?.id, status: 'submitting'})

    const r = await axiosClient.post<any, ISubmitPdfJobResponse>(
      `${dev?.localSAM ? '' : '/Prod'}/pdf/submitJob?host=${host}&env=${env}${dev?.localSAM ? '&go' : ''}`,
      queryPayload
    )

    runInAction(() => {
      serverJob.jobId = r.data.jobId
      serverJob.status = 'accepted'
      console.log({r, serverJob})
    })

    return serverJob
  }

  @computed get availableTemplatesForCatalogState() {
    const {catalog: {isShowingAssortment, isGroupedView}} = this
    return isShowingAssortment ? ['assortment'] : isGroupedView ? ['catalogItems_grouped'] : 'catalogItems'
  }

  @computed get defaultTemplate(): ServerPDFTemplate {
    const {
        stores: {users: {user: {id: userId}}}, catalog: {index},
        templatesById, prepareGeneratePDF, stores, catalog: {pdfCatalogStore: {availableTemplates}}
      } = this,
      template = _.first([...availableTemplates.keys()])

    return templatesById[template] ? templatesById[template](stores) : null
  }

  @computed get pdfJobForThisTab(): CatalogPDFTemplateJobPayload {
    if (!this.stores.tab?.includeInPDF || _.isEmpty(this.availableTemplates)) return null

    const {
        catalog: {index}, templatesById, stores, catalog: {pdfCatalogStore: {availableTemplates}}
      } = this,
      template = _.first(Array.from(availableTemplates.keys())) as CatalogPDFTemplateNames,
      templateFactory = templatesById[template]

    if (!templateFactory) {
      throw new Error(`Unable to find template factory for template ${template}`)
    }

    const t: ServerPDFTemplate = templateFactory(stores)

    return {
      index,
      template,
      body: _.cloneDeep(t.esQuery),
      settings: _.cloneDeep(t.settings) as CatalogSerializableSettings
    }
  }

  @action prepareGenerateCatalogPDF = async (propsTemplate?: PDFTemplateName) => {
    const {
        stores: {users: {user: {id: userId}}}, catalog: {index},
        templatesById, prepareGeneratePDF, stores, catalog: {pdfCatalogStore: {availableTemplates}}
      } = this,
      template = propsTemplate ?? _.first(Array.from(availableTemplates.keys())),
      t: ServerPDFTemplate = templatesById[template](stores)

    const r = await prepareGeneratePDF({
      index,
      template,
      userId,
      body: _.cloneDeep(t.esQuery),
      settings: t.settings,
      filename: t.defaultPdfFilename
    })

    return r
  }

  @action generateCatalogPDF = async (propsTemplate?: PDFTemplateName) => {
    const {templatesById, stores, catalog: {pdfCatalogStore: {availableTemplates}}} = this,
      template = propsTemplate ?? _.first(Array.from(availableTemplates.keys()))

    if (!template) {
      console.error(`No template available or specified`)
      return
    }

    if (!templatesById[template]) {
      console.error(`Unknown PDF template specified:  '${template}'`)
      return
    }

    const t: ServerPDFTemplate = templatesById[template](stores)

    return await t.submitPDFJob()

    // TODO
    // General product template
    // Group By Template
    throw new Error(`Unhandled generate catalog PDF case - no template determined for current catalog state`)
  }

  @action invalidateArtifacts = async () => {
    CloudPDFStore.generatedPdfs.clear()
    await this.stores.queryClient.invalidateQueries(QUERY_KEY_PDF_ARTIFACTS)
  }

  @computed get pdfByTemplate(): Record<PDFTemplateName, Array<CloudPDFEntry>> {
    return _.groupBy(this.pdfsTimestampDesc, v => v.template)
  }

  @action removeJob = (job: IQueuedPdfServerJob) => {
    this.stores.currentUser.pendingPdfJobs.remove(job)
  }

}






// WEBPACK FOOTER //
// ./src/modules/pdf/serverRenderedPDF/CloudPDFStore.ts